import React, { useState } from "react";
import { Button } from "@/dentlab/src/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/dentlab/src/components/ui/dropdown-menu";
import { iconCategories, renderIcon, iconLabels } from "../utils/icon-mapper";
import { X } from "lucide-react";
import { cn } from "@/dentlab/src/lib/util";

interface IconSelectorProps {
	currentIcon?: string;
	onIconSelect: (icon: string | undefined) => void;
	disabled?: boolean;
	label?: string;
}

export const IconSelector: React.FC<IconSelectorProps> = ({
	currentIcon,
	onIconSelect,
	disabled = false,
	label = "Tab-Icon wählen"
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleIconSelect = (iconName: string | undefined) => {
		onIconSelect(iconName);
		setIsOpen(false);
	};

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="h-8 px-2"
					disabled={disabled}
				>
					{currentIcon ? (
						renderIcon(currentIcon, { className: "h-4 w-4" })
					) : (
						<span className="text-xs text-muted-foreground">Kein Icon</span>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-96 p-4 max-h-[500px] overflow-y-auto">
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<div className="text-sm font-medium">{label}</div>
						{currentIcon && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => handleIconSelect(undefined)}
								className="h-7 text-xs"
							>
								<X className="h-3 w-3 mr-1" />
								Icon entfernen
							</Button>
						)}
					</div>

					{/* Icon categories */}
					{Object.entries(iconCategories).map(([category, icons]) => (
						<div key={category}>
							<div className="text-xs font-medium text-gray-600 mb-2">{category}</div>
							<div className="grid grid-cols-6 gap-1">
								{icons.map((iconName) => (
									<button
										key={iconName}
										onClick={() => handleIconSelect(iconName)}
										className={cn(
											"p-2 rounded hover:bg-gray-100 flex flex-col items-center justify-center gap-1 transition-colors",
											currentIcon === iconName && "bg-blue-50 ring-1 ring-blue-500"
										)}
										title={iconLabels[iconName] || iconName}
									>
										{renderIcon(iconName, { className: "h-4 w-4" })}
										<span className="text-[10px] text-gray-600 truncate max-w-full">
											{iconLabels[iconName] ? 
												(iconLabels[iconName].length > 10 ? 
													iconLabels[iconName].substring(0, 8) + "..." : 
													iconLabels[iconName]) : 
												iconName}
										</span>
									</button>
								))}
							</div>
						</div>
					))}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};